import type {PseudoStateKey} from '@packages/systems/core/utils/StyleTypes';

/**
 * Used to apply fake pseudo states (e.g. .some-selector:hover, .some-selector.-wfp-hover {} )
 * This must be different from "wf-" so that DomUtils.getWfClasses() doesn't mistake it for other UI-changing classes (e.g. "wf-empty").
 */
export const PSEUDO_PREFIX = '-wfp-';

/**
 * Prepended to Webflow-specific derived states like "Current" (for links) and "Open" (for dropdowns) to avoid collisions with user classes.
 */
export const STATE_PREFIX = 'w--';

export const STATES = {
  OPEN: 'open' as const,
  CURRENT: 'current' as const,
} as const;

/**
 * For custom controls we are listening to pseudo state :checked or :focus invoked on `input` node (type="checkbox" or type="radio"),
 * but as a result modifying other nodes by the class created based on states from this list. This state is added to style rule in customNodeState field,
 * and cause appearance of the class with prefix 'w--'
 *
 * Read more about custom controls: https://github.com/webflow/webflow/blob/dev/docs/app/designer-elements/form/custom-controls.md
 */
export const REDIRECTED_PSEUDOS = {
  CHECKED: 'redirected-checked' as const,
  FOCUS: 'redirected-focus' as const,
  FOCUS_VISIBLE: 'redirected-focus-visible' as const,
  ECOMMERCE_PILL_SELECTED: 'ecommerce-pill-selected' as const,
  ECOMMERCE_PILL_DISABLED: 'ecommerce-pill-disabled' as const,
  ECOMMERCE_ADD_TO_CART_DISABLED: 'ecommerce-add-to-cart-disabled' as const,
  ECOMMERCE_BUY_NOW_DISABLED: 'ecommerce-buy-now-disabled' as const,
} as const;

export const REDIRECTED_PSEUDOS_ORDER = [
  REDIRECTED_PSEUDOS.CHECKED,
  REDIRECTED_PSEUDOS.FOCUS,
  REDIRECTED_PSEUDOS.FOCUS_VISIBLE,
  REDIRECTED_PSEUDOS.ECOMMERCE_PILL_SELECTED,
  REDIRECTED_PSEUDOS.ECOMMERCE_PILL_DISABLED,
  REDIRECTED_PSEUDOS.ECOMMERCE_ADD_TO_CART_DISABLED,
  REDIRECTED_PSEUDOS.ECOMMERCE_BUY_NOW_DISABLED,
] as const;

export const STATES_ORDER = [
  STATES.OPEN,
  STATES.CURRENT,
  ...REDIRECTED_PSEUDOS_ORDER,
] as const;

type PseudosObjectKeys =
  | 'ODD_CHILD'
  | 'EVEN_CHILD'
  | 'FIRST_CHILD'
  | 'LAST_CHILD'
  | 'HOVER'
  | 'ACTIVE'
  | 'DISABLED'
  | 'FOCUS'
  | 'FOCUS_VISIBLE'
  | 'FOCUS_WITHIN'
  | 'VISITED'
  | 'PLACEHOLDER'
  | 'LANG'
  | 'POPOVER_OPEN'
  | 'BACKDROP';
export const PSEUDOS: Partial<Record<PseudosObjectKeys, PseudoStateKey>> = {
  ODD_CHILD: 'nth-child(odd)',
  EVEN_CHILD: 'nth-child(even)',
  FIRST_CHILD: 'first-child',
  LAST_CHILD: 'last-child',
  HOVER: 'hover',
  ACTIVE: 'active',
  DISABLED: 'disabled',
  FOCUS: 'focus',
  FOCUS_VISIBLE: 'focus-visible',
  FOCUS_WITHIN: 'focus-within',
  VISITED: 'visited',
  PLACEHOLDER: 'placeholder',
  LANG: 'lang',
  POPOVER_OPEN: 'popover-open',
  BACKDROP: 'backdrop',
};

// Structure styles (e.g. first child) should come first, so that styles like hover will apply to those children as well
// As well, odd/even must come before first/last, so that people can style the first item separately from the odd items (as 1 is an odd number)
// popover-open must come before the interactive states (hover/active/focus) so properties authored in both win on the interactive rule
export const PSEUDOS_ORDER = [
  PSEUDOS.ODD_CHILD,
  PSEUDOS.EVEN_CHILD,
  PSEUDOS.FIRST_CHILD,
  PSEUDOS.LAST_CHILD,
  PSEUDOS.POPOVER_OPEN,
  PSEUDOS.VISITED,
  PSEUDOS.DISABLED,
  PSEUDOS.HOVER,
  PSEUDOS.ACTIVE,
  PSEUDOS.FOCUS_WITHIN,
  PSEUDOS.FOCUS,
  PSEUDOS.FOCUS_VISIBLE,
  PSEUDOS.PLACEHOLDER,
  PSEUDOS.LANG,
  PSEUDOS.BACKDROP,
] as const;

export const COMBINATORS = {
  NONE: '',
  DESCENDANT: '|', // e.g. .button .cart
  COMBO: '&', // e.g. .button.cart
} as const;
